import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { auth, signOut } from "./firebase";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { 
  Calendar, Users, Trophy, Home as HomeIcon, User, ChevronRight, MapPin, Clock, 
  Shield, Loader2, LogOut, Crown, Gift, Search, Copy, Share2, Filter, ChevronDown, Settings, Camera, X, Menu, Target, Star, Zap, Lock, Sparkles, Award, BarChart3, MessageSquare
} from "lucide-react";
import { getDisplayName, getDisplayShortName } from "./utils/teamNameMapping";

// Import page components
import TeamBuilderPage from "./pages/TeamBuilderPage";
import LeaguesPage from "./pages/LeaguesPage";
import PublicLeaguesPage from "./pages/PublicLeaguesPage";
import LeagueDetailPage from "./pages/LeagueDetailPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import AdminPage from "./pages/AdminPage";
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";
import AdminEmailsPage from "./pages/AdminEmailsPage";
import AdminAudiencePage from "./pages/AdminAudiencePage";
import PredictorPage from "./pages/PredictorPage";
import MatchDayPage from "./pages/MatchDayPage";
import DailyTeamBuilderPage from "./pages/DailyTeamBuilderPage";
import DailyHistoryPage from "./pages/DailyHistoryPage";
import FeedbackPage from "./pages/FeedbackPage";
import PrivacyPage from "./pages/PrivacyPage";
import SubscribePage from "./pages/SubscribePage";
import DailyReward from "./components/DailyReward";
import GameplayTutorial from "./components/GameplayTutorial";
import CelebrationOverlay from "./components/CelebrationOverlay";
import TopBar from "./components/TopBar";
import PointSystemPage from "./pages/PointSystemPage";
import SettingsPage from "./pages/SettingsPage";
import FootballPitchPreviewPage from "./pages/football/FootballPitchPreviewPage";
import AdBanner, { setAdBannerVisible } from "./components/AdBanner";
import { setAdMobReady, setAdMobFailed } from "./utils/adMobState";
import AuthPage from "./components/AuthPage";
import { registerPushNotifications } from "./utils/pushNotifications";
import { setupDeepLinkHandler } from "./utils/deepLinks";
import { hapticSuccess, hapticLight } from "./utils/haptics";
import { SportProvider, useSport } from "./contexts/SportContext";
import SportNavigator from "./components/shared/SportNavigator";
import LaunchInterstitial from "./components/shared/LaunchInterstitial";
import CountryModal from "./components/shared/CountryModal";
import FootballHomePage from "./pages/football/FootballHomePage";
import HowFootballWorksPage from "./pages/football/HowFootballWorksPage";
import FootballSquadBuilderPage from "./pages/football/FootballSquadBuilderPage";
import FootballPredictorPage from "./pages/football/FootballPredictorPage";
import FootballPredictorHistoryPage from "./pages/football/FootballPredictorHistoryPage";
import FootballMatchDayResultsPage from "./pages/football/FootballMatchDayResultsPage";
import FootballLeaderboardPage from "./pages/football/FootballLeaderboardPage";
import FootballMatchesPage from "./pages/football/FootballMatchesPage";
import FootballFixtureDetailPage from "./pages/football/FootballFixtureDetailPage";
import FootballPointSystemPage from "./pages/football/FootballPointSystemPage";
import FootballLeaguesPage from "./pages/football/FootballLeaguesPage";
import FootballLeagueDetailPage from "./pages/football/FootballLeagueDetailPage";
import AdminFootballMatchDayPage from "./pages/football/AdminFootballMatchDayPage";
import AdminFootballUsersPage from "./pages/football/AdminFootballUsersPage";
import AdminFootballSquadsPage from "./pages/football/AdminFootballSquadsPage";
import AdminFootballWeeksPage from "./pages/football/AdminFootballWeeksPage";
import MiniCupPage from "./pages/football/MiniCupPage";
import AdminMiniCupSponsorPage from "./pages/football/AdminMiniCupSponsorPage";
import FootballProfileStats from "./components/football/FootballProfileStats";
import FootballReferralPanel from "./components/football/FootballReferralPanel";
import WhatsNewModal from "./components/shared/WhatsNewModal";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Team colors
const TEAM_COLORS = {
  "India": { bg: "from-orange-500 to-blue-600", accent: "#FF9933" },
  "Pakistan": { bg: "from-green-600 to-green-800", accent: "#01411C" },
  "Australia": { bg: "from-yellow-400 to-green-600", accent: "#FFCD00" },
  "England": { bg: "from-blue-700 to-red-600", accent: "#003087" },
  "South Africa": { bg: "from-green-500 to-yellow-500", accent: "#007749" },
  "New Zealand": { bg: "from-black to-gray-700", accent: "#000000" },
  "West Indies": { bg: "from-red-600 to-yellow-500", accent: "#7B0041" },
  "Sri Lanka": { bg: "from-blue-800 to-yellow-500", accent: "#0021A5" },
  "Afghanistan": { bg: "from-red-600 to-green-600", accent: "#D32011" },
  "default": { bg: "from-gray-600 to-gray-800", accent: "#4B5563" }
};

const getTeamColor = (team) => TEAM_COLORS[team] || TEAM_COLORS["default"];

const ROLE_COLORS = {
  "Batsman": "bg-blue-500/20 text-blue-400",
  "Bowler": "bg-red-500/20 text-red-400",
  "All-Rounder": "bg-purple-500/20 text-purple-400",
  "Wicketkeeper": "bg-amber-500/20 text-amber-400"
};

// Navigation Component with Logo
const Navigation = () => {
  const location = useLocation();
  const { user, dbUser } = useAuth();
  const { sport } = useSport();
  const [menuOpen, setMenuOpen] = useState(false);

  // Cricket nav - 6 tabs
  const cricketNavItems = [
    { path: "/", icon: HomeIcon, label: "Home" },
    { path: "/daily", icon: Zap, label: "Match Day" },
    { path: "/predictor", icon: Target, label: "Predictor" },
    { path: "/my-team", icon: Shield, label: "My Team" },
    { path: "/leagues", icon: Crown, label: "Leagues" },
    { path: "/leaderboard", icon: Trophy, label: "Ranks" },
  ];

  // Football nav - 6 tabs (FIFA WC 2026)
  const footballNavItems = [
    { path: "/", icon: HomeIcon, label: "Home" },
    { path: "/football/matches", icon: Calendar, label: "Matches" },
    { path: "/football/predictor", icon: Target, label: "Predictor" },
    { path: "/football/squad-builder", icon: Shield, label: "Squad" },
    { path: "/football/leagues", icon: Crown, label: "Leagues" },
    { path: "/football/leaderboard", icon: Trophy, label: "Ranks" },
  ];

  const mainNavItems = sport === "football" ? footballNavItems : cricketNavItems;
  const activeColor = sport === "football" ? "text-amber-400" : "text-emerald-400";

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Bottom Navigation Bar - 6 tabs (sport-aware) */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-slate-700/50 z-50" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }} data-testid={`bottom-nav-${sport}`}>
        <div className="max-w-7xl mx-auto px-1">
          <div className="flex justify-around items-center py-1">
            {mainNavItems.map(({ path, icon: Icon, label }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                className={`flex flex-col items-center gap-0.5 px-1.5 py-2 rounded-lg transition-all min-w-[48px] ${
                  isActive(path)
                    ? activeColor
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                <Icon size={20} />
                <span className="text-[9px] font-medium">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Countdown Timer Component
const CountdownTimer = ({ targetDate, matchInfo }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const diff = target - now;

      if (diff > 0) {
        setExpired(false);
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          mins: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          secs: Math.floor((diff % (1000 * 60)) / 1000)
        });
      } else {
        setExpired(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  if (expired) {
    return (
      <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-xl p-4" data-testid="countdown-timer">
        <p className="text-emerald-400 text-xs font-medium mb-1">MATCH STARTED</p>
        <p className="text-white text-sm font-semibold">{matchInfo}</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4" data-testid="countdown-timer">
      <p className="text-amber-400 text-xs font-medium mb-2">⏰ NEXT MATCH</p>
      <p className="text-white text-sm font-semibold mb-3">{matchInfo}</p>
      <div className="flex gap-3">
        {[
          { val: timeLeft.days, label: 'Days' },
          { val: timeLeft.hours, label: 'Hrs' },
          { val: timeLeft.mins, label: 'Min' },
          { val: timeLeft.secs, label: 'Sec' }
        ].map(({ val, label }) => (
          <div key={label} className="text-center">
            <div className="bg-slate-900/80 rounded-lg px-3 py-2 min-w-[45px]">
              <span className="text-xl font-bold text-white font-mono">{String(val).padStart(2, '0')}</span>
            </div>
            <span className="text-[10px] text-slate-400 mt-1">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Week 2 Announcement Banner Component
const TransferBanner = ({ onDismiss, windowName }) => {
  return (
    <div className="relative bg-gradient-to-r from-emerald-600/20 via-cyan-600/20 to-blue-600/20 border border-emerald-500/30 rounded-xl p-4 mb-4" data-testid="week2-announcement-banner">
      <button 
        onClick={onDismiss}
        className="absolute top-2 right-2 text-slate-400 hover:text-white p-1"
        data-testid="dismiss-week2-banner"
      >
        <X size={18} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
          <Gift className="text-white" size={20} />
        </div>
        <div className="flex-1 pr-6">
          <h3 className="text-white font-bold mb-1">Transfer System is LIVE!</h3>
          <div className="text-sm text-slate-300 space-y-1">
            <p><span className="text-emerald-400 font-medium">Your Budget:</span> Team Value + Savings + 5 Cr Bonus</p>
            <p><span className="text-cyan-400 font-medium">Refer a Friend:</span> You get +25 pts & +1 Cr, they get +5 Cr budget!</p>
            <p className="text-xs text-slate-400">Max 3 referrals per week (resets each transfer week). Transfer window is open!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Team Value Card Component (Sharable)
const TeamValueCard = ({ userTeam, budgetInfo }) => {
  const [copying, setCopying] = useState(false);
  
  if (!userTeam || !budgetInfo) return null;
  
  // Fix: Use correct field names from API response
  // current_team_cost = current dynamic value of team, team_value_purchased = original purchase price
  const currentValue = budgetInfo.breakdown?.current_team_cost || budgetInfo.breakdown?.snapshot_team_value || 0;
  const purchaseValue = budgetInfo.breakdown?.team_value_purchased || 0;
  const valueGained = currentValue - purchaseValue;
  
  // Handle cases where calculation might result in NaN
  const displayValueGained = isNaN(valueGained) ? 0 : valueGained;
  
  const shareText = `My APL Fantasy Team Value!\nTeam: ${userTeam.name}\nCurrent Value: ${currentValue} Cr\nValue Gained: ${displayValueGained > 0 ? '+' : ''}${displayValueGained.toFixed(1)} Cr\nWeek 2 Budget: ${budgetInfo.budget} Cr\n\nJoin APL: ${window.location.origin}`;
  
  const handleShare = async () => {
    setCopying(true);
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My APL Team Value', text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
      }
    } catch (e) {
      console.error("Share failed:", e);
    } finally {
      setCopying(false);
    }
  };
  
  return (
    <div className="bg-gradient-to-br from-violet-600/20 via-purple-600/20 to-fuchsia-600/20 border border-violet-500/30 rounded-xl p-4" data-testid="team-value-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="text-violet-400" size={20} />
          <h3 className="text-white font-bold">{userTeam.name}</h3>
        </div>
        <button 
          onClick={handleShare}
          className="text-violet-400 hover:text-violet-300 p-2 rounded-lg bg-violet-500/10 hover:bg-violet-500/20 transition-all flex items-center gap-1 text-sm"
          data-testid="share-value-btn"
        >
          {copying ? <Loader2 className="animate-spin" size={14} /> : <Share2 size={14} />}
          Share
        </button>
      </div>
      
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-white">{currentValue}</p>
          <p className="text-[10px] text-slate-400">Team Value (Cr)</p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <p className={`text-lg font-bold ${displayValueGained >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {displayValueGained > 0 ? '+' : ''}{displayValueGained.toFixed(1)}
          </p>
          <p className="text-[10px] text-slate-400">Value Gained (Cr)</p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-cyan-400">{budgetInfo.budget}</p>
          <p className="text-[10px] text-slate-400">Week 2 Budget</p>
        </div>
      </div>
      
      {budgetInfo.breakdown && (
        <div className="mt-3 pt-3 border-t border-slate-700/50 text-xs text-slate-400">
          <p>Savings: {budgetInfo.breakdown.bank_balance} Cr • Bonus: +{budgetInfo.breakdown.week_bonus} Cr</p>
        </div>
      )}
    </div>
  );
};

// Coming Soon Home - APL 2026
const ComingSoonHome = () => {
  const { user } = useAuth();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });

  useEffect(() => {
    const SEASON_START = new Date("2026-03-26T14:00:00Z");
    const tick = () => {
      const diff = SEASON_START.getTime() - Date.now();
      if (diff <= 0) { setTimeLeft({ days: 0, hours: 0, mins: 0, secs: 0 }); return; }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        mins: Math.floor((diff % 3600000) / 60000),
        secs: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="coming-soon-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-500 p-6 md:p-10 text-center">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }}></div>
        <div className="relative z-10">
          <img src="/logo.png" alt="APL" className="w-20 h-20 mx-auto rounded-2xl shadow-lg mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">APL 2026</h1>
          <p className="text-lg text-white/90 font-medium mb-1">Coming March 2026</p>
          <p className="text-sm text-white/70">Apna Premier League - Fantasy Cricket</p>
        </div>
      </div>

      {/* Countdown Timer */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 text-center" data-testid="ipl-countdown">
        <p className="text-amber-400 text-sm font-semibold mb-4 tracking-wider">COUNTDOWN TO APL 2026</p>
        <div className="flex justify-center gap-4">
          {[
            { val: timeLeft.days, label: "Days" },
            { val: timeLeft.hours, label: "Hours" },
            { val: timeLeft.mins, label: "Mins" },
            { val: timeLeft.secs, label: "Secs" },
          ].map(({ val, label }) => (
            <div key={label} className="text-center">
              <div className="bg-slate-900 rounded-xl px-4 py-3 min-w-[60px] border border-slate-700">
                <span className="text-3xl font-bold text-white font-mono">{String(val).padStart(2, "0")}</span>
              </div>
              <span className="text-xs text-slate-400 mt-1.5 block">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Links Section */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5">
        <h2 className="text-lg font-bold text-white mb-1">Quick Links</h2>
        <p className="text-slate-400 text-sm mb-4">Jump to your favorite sections</p>
        <div className="space-y-2">
          <Link to="/leaderboard" className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3.5 hover:border-emerald-500/40 transition-all group" data-testid="cs-link-leaderboard">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Trophy className="text-emerald-400" size={18} />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">Leaderboard</p>
              <p className="text-slate-400 text-xs">Overall & weekly standings</p>
            </div>
            <ChevronRight className="text-slate-500 group-hover:text-emerald-400 transition-colors" size={16} />
          </Link>
          <Link to="/daily" className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3.5 hover:border-amber-500/40 transition-all group" data-testid="cs-link-matchday">
            <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Zap className="text-amber-400" size={18} />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">Match Day</p>
              <p className="text-slate-400 text-xs">Per-match results & daily champions</p>
            </div>
            <ChevronRight className="text-slate-500 group-hover:text-amber-400 transition-colors" size={16} />
          </Link>
          <Link to="/predictor" className="flex items-center gap-3 bg-purple-500/10 border border-purple-500/20 rounded-xl p-3.5 hover:border-purple-500/40 transition-all group" data-testid="cs-link-predictor">
            <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Target className="text-purple-400" size={18} />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">Predictor</p>
              <p className="text-slate-400 text-xs">Cap predictions & accuracy rankings</p>
            </div>
            <ChevronRight className="text-slate-500 group-hover:text-purple-400 transition-colors" size={16} />
          </Link>
        </div>
      </div>

      {/* Feedback CTA */}
      <div className="bg-gradient-to-br from-cyan-600/20 to-emerald-600/20 border border-cyan-500/30 rounded-2xl p-5 text-center">
        <h2 className="text-lg font-bold text-white mb-2">Help Shape APL 2026!</h2>
        <p className="text-slate-400 text-sm mb-4">Tell us what features you want, rate the app, and share your thoughts.</p>
        <Link
          to="/feedback"
          className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold px-6 py-2.5 rounded-xl hover:opacity-90 transition-all text-sm"
          data-testid="cs-feedback-cta"
        >
          Share Feedback <ChevronRight size={16} />
        </Link>
      </div>

      {!user && (
        <div className="text-center">
          <Link to="/auth" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold px-6 py-3 rounded-xl hover:bg-slate-100 transition-all text-sm" data-testid="cs-login-btn">
            Login / Sign Up <ChevronRight size={16} />
          </Link>
        </div>
      )}
    </div>
  );
};

// Home Page - Compact Hero
const HomePage = () => {
  const { user, dbUser } = useAuth();
  const [stats, setStats] = useState({ matches: 0, players: 0, users: 0, teams: 0, leagues: 0 });
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [userTeam, setUserTeam] = useState(null);
  const [transferWindow, setTransferWindow] = useState({ is_open: false, window: null });
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [nextMatch, setNextMatch] = useState(null);
  const [showWeek2Banner, setShowWeek2Banner] = useState(false);
  const [budgetInfo, setBudgetInfo] = useState(null);
  const [matchesWithPredictions, setMatchesWithPredictions] = useState([]);
  const [todayMatches, setTodayMatches] = useState([]);
  const [showMatchDayAnnouncement, setShowMatchDayAnnouncement] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [appConfig, setAppConfig] = useState({ winners_announced: false, coming_soon: false });
  const [winnersData, setWinnersData] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isNativePlatform, setIsNativePlatform] = useState(false);

  const [noTeamBannerDismissed, setNoTeamBannerDismissed] = useState(
    () => !!localStorage.getItem("apl_no_team_banner_dismissed")
  );
  const dismissNoTeamBanner = () => {
    localStorage.setItem("apl_no_team_banner_dismissed", "1");
    setNoTeamBannerDismissed(true);
  };

  const showNoTeamBanner =
    !!user &&
    !userTeam &&
    !dbUser?.daily_matches_played &&
    !noTeamBannerDismissed;

  const WHATS_NEW_VERSION = "ipl2026_week4";

  useEffect(() => {
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        setIsNativePlatform(Capacitor.isNativePlatform());
      } catch { setIsNativePlatform(false); }
    })();
  }, []);

  const fetchHomeData = useCallback(async () => {
      try {
        // Fetch app config first
        const configRes = await axios.get(`${API}/app-config`).catch(() => null);
        const cfg = configRes?.data || { winners_announced: false, coming_soon: false };
        setAppConfig(cfg);

        // If winners announced and not dismissed, fetch winners data
        if (cfg.winners_announced) {
          const celebSeen = localStorage.getItem("apl_celebration_seen_home");
          if (!celebSeen) {
            const winnersRes = await axios.get(`${API}/winners`).catch(() => null);
            if (winnersRes?.data) {
              setWinnersData(winnersRes.data);
              setShowCelebration(true);
            }
          }
        }

        // If coming_soon mode, skip loading regular homepage data
        if (cfg.coming_soon) {
          setLoading(false);
          return;
        }

        // Use consolidated home endpoint — one call instead of 5+
        const headers = {};
        if (user) {
          try {
            const idToken = await user.getIdToken();
            headers.Authorization = `Bearer ${idToken}`;
          } catch (e) { /* not logged in */ }
        }
        const homeRes = await axios.get(`${API}/home`, { headers });
        const data = homeRes.data;

        setStats(data.stats);
        const matches = data.matches;
        setMatchesWithPredictions(data.prediction_match_ids || []);
        setTransferWindow(data.transfer_window);
        setTodayMatches(data.today_matches || []);

        if (data.team) {
          setUserTeam(data.team);
          // Fetch full budget data (home endpoint has simplified team_value only)
          try {
            const idToken = await user.getIdToken();
            const budgetRes = await axios.get(`${API}/budget/my-budget`, { headers: { Authorization: `Bearer ${idToken}` } });
            if (budgetRes?.data) setBudgetInfo(budgetRes.data);
          } catch (e) { /* budget fetch optional */ }
        }
        
        // Helper to parse match date
        const parseMatchDate = (match) => {
          const dateStr = match.date.replace(/^[A-Za-z]+, /, '');
          const [hours, mins] = (match.time_gmt || "00:00").split(':');
          return new Date(`${dateStr} ${hours}:${mins}:00 GMT`);
        };
        
        // Filter to only truly future matches
        const now = new Date();
        const futureMatches = matches.filter(m => parseMatchDate(m) > now);
        setUpcomingMatches(futureMatches.slice(0, 3));
        
        // Set the next match for countdown (first match that hasn't started yet)
        if (futureMatches.length > 0) {
          const match = futureMatches[0];
          // Use team name mapping for short codes
          const team1Short = getDisplayShortName(match.team1_short) || match.team1_short;
          const team2Short = getDisplayShortName(match.team2_short) || match.team2_short;
          setNextMatch({
            date: parseMatchDate(match),
            info: `${team1Short} vs ${team2Short} • ${match.match_type || `Match ${match.match_number}`}`
          });
        } else {
          setNextMatch(null);
        }
      } catch (e) {
        console.error("Failed to fetch data:", e);
      } finally {
        setLoading(false);
      }
  }, [user]);

  useEffect(() => {
    fetchHomeData();
    
    // Auto-refresh match data every 15 minutes
    const refreshInterval = setInterval(fetchHomeData, 15 * 60 * 1000);
    
    // Check if first time user
    const hasSeenOnboarding = localStorage.getItem('apl_onboarding_seen');
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    }
    
    // Check if Week 2 banner was dismissed
    const week2BannerDismissed = localStorage.getItem('apl_week2_banner_dismissed');
    if (!week2BannerDismissed) {
      setShowWeek2Banner(true);
    }
    
    // Check if Match Day announcement was seen
    const matchDayAnnouncementSeen = localStorage.getItem('apl_matchday_announcement_seen');
    if (!matchDayAnnouncementSeen) {
      setShowMatchDayAnnouncement(true);
    }

    // What's New popup is now handled by the shared multi-sport WhatsNewModal
    // (rendered in HomeRouter) so it shows on both football and cricket home.

    return () => clearInterval(refreshInterval);
  }, [fetchHomeData]);

  const dismissOnboarding = () => {
    localStorage.setItem('apl_onboarding_seen', 'true');
    setShowOnboarding(false);
  };
  
  const dismissWeek2Banner = () => {
    localStorage.setItem('apl_week2_banner_dismissed', 'true');
    setShowWeek2Banner(false);
  };

  const dismissMatchDayAnnouncement = () => {
    localStorage.setItem('apl_matchday_announcement_seen', 'true');
    setShowMatchDayAnnouncement(false);
  };

  const dismissWhatsNew = () => {
    localStorage.setItem('apl_whats_new_seen', WHATS_NEW_VERSION);
    setShowWhatsNew(false);
    setAdBannerVisible(true);
  };

  const dismissCelebration = () => {
    localStorage.setItem('apl_celebration_seen_home', 'true');
    setShowCelebration(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }

  return (
    <div>
    <div className="space-y-6" data-testid="home-page">
      {/* Celebration Overlay */}
      {showCelebration && winnersData && (
        <CelebrationOverlay
          winnersData={winnersData}
          pageType="leaderboard"
          onDismiss={dismissCelebration}
        />
      )}

      {/* Coming Soon Mode */}
      {appConfig.coming_soon && (
        <>
          <ComingSoonHome />
          {user && <DailyReward />}
        </>
      )}

      {/* Regular Homepage (only when not in coming_soon mode) */}
      {!appConfig.coming_soon && (<>
      {/* First-time User Onboarding Overlay */}
      {showOnboarding && dbUser && dbUser.has_seen_tutorial !== false && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full">
            <img src="/logo.png" alt="APL" className="w-16 h-16 mx-auto mb-4 rounded-xl" />
            <h2 className="text-2xl font-bold text-white text-center mb-4">Welcome to APL!</h2>
            <div className="space-y-4 text-sm text-slate-300">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="text-emerald-400" size={16} />
                </div>
                <div>
                  <p className="font-semibold text-white">1. Create Your Team</p>
                  <p className="text-slate-400">Pick 11 players from 300+ cricketers (when transfer window is open)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <Crown className="text-cyan-400" size={16} />
                </div>
                <div>
                  <p className="font-semibold text-white">2. Join Mini Leagues</p>
                  <p className="text-slate-400">Compete with friends in private leagues</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <Gift className="text-yellow-400" size={16} />
                </div>
                <div>
                  <p className="font-semibold text-white">3. Refer & Earn</p>
                  <p className="text-slate-400">Get 25 pts + 1 Cr for each friend you invite</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Trophy className="text-purple-400" size={16} />
                </div>
                <div>
                  <p className="font-semibold text-white">4. Climb the Leaderboard</p>
                  <p className="text-slate-400">Earn points based on your players' real match performance</p>
                </div>
              </div>
            </div>
            <button
              onClick={dismissOnboarding}
              className="w-full mt-6 bg-emerald-500 text-white font-semibold py-3 rounded-lg hover:bg-emerald-600 transition-all"
            >
              Got it, let's go!
            </button>
          </div>
        </div>
      )}

      {/* What's New Release Notes Popup */}
      {showWhatsNew && !showOnboarding && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 max-w-sm w-full" data-testid="whats-new-popup">
            <div className="text-center mb-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-2">
                <Sparkles className="text-white" size={24} />
              </div>
              <h2 className="text-lg font-bold text-white">What's New in APL 2026</h2>
              <p className="text-slate-400 text-xs mt-1">IPL is in full swing. Here's what's new:</p>
            </div>

            <div className="space-y-2.5">
              <Link to="/leagues" onClick={dismissWhatsNew} className="flex items-center gap-3 p-2.5 bg-emerald-500/5 border border-emerald-500/20 rounded-xl hover:border-emerald-500/40 transition-all">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Trophy className="text-emerald-400" size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm">Leagues unlock as you play</p>
                  <p className="text-slate-400 text-xs">Build your season team and play a Match Day to unlock league features</p>
                </div>
                <ChevronRight size={16} className="text-slate-500 flex-shrink-0" />
              </Link>

              <Link to="/leagues" onClick={dismissWhatsNew} className="flex items-center gap-3 p-2.5 bg-purple-500/5 border border-purple-500/20 rounded-xl hover:border-purple-500/40 transition-all">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Crown className="text-purple-400" size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm">See weekly winners in your leagues</p>
                  <p className="text-slate-400 text-xs">Track week-over-week performance and cap holders inside every league</p>
                </div>
                <ChevronRight size={16} className="text-slate-500 flex-shrink-0" />
              </Link>

              <Link to="/predictor" onClick={dismissWhatsNew} className="flex items-center gap-3 p-2.5 bg-cyan-500/5 border border-cyan-500/20 rounded-xl hover:border-cyan-500/40 transition-all">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="text-cyan-400" size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm">Unlock more predictor questions</p>
                  <p className="text-slate-400 text-xs">Now up to 10 ad-unlocks per match for bonus points</p>
                </div>
                <ChevronRight size={16} className="text-slate-500 flex-shrink-0" />
              </Link>

              <div className="flex items-center gap-3 p-2.5 bg-orange-500/5 border border-orange-500/20 rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                  <Gift className="text-orange-400" size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm">Daily Rewards</p>
                  <p className="text-slate-400 text-xs">Log in daily for points and streak bonuses</p>
                </div>
              </div>

              <Link to="/subscribe" onClick={dismissWhatsNew} className="flex items-center gap-3 p-2.5 bg-amber-500/5 border border-amber-500/20 rounded-xl hover:border-amber-500/40 transition-all">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Star className="text-amber-400" size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm">Go ad-free with Pro</p>
                  <p className="text-slate-400 text-xs">Extra budget, exclusive features, cleaner experience</p>
                </div>
                <ChevronRight size={16} className="text-slate-500 flex-shrink-0" />
              </Link>
            </div>

            <button
              onClick={dismissWhatsNew}
              className="w-full mt-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-2.5 rounded-xl hover:opacity-90 transition-all text-sm"
              data-testid="dismiss-whats-new"
            >
              Let's Go!
            </button>
          </div>
        </div>
      )}

      {/* Week 2 Announcement Banner */}
      {showWeek2Banner && transferWindow.is_open && (
        <TransferBanner onDismiss={dismissWeek2Banner} windowName={transferWindow?.window?.name} />
      )}

      {/* No-team onboarding banner */}
      {showNoTeamBanner && (
        <div className="relative bg-slate-800/80 border border-emerald-500/30 rounded-xl p-4 mb-4" data-testid="no-team-banner">
          <button
            onClick={dismissNoTeamBanner}
            className="absolute top-3 right-3 text-slate-500 hover:text-white text-lg leading-none"
            data-testid="dismiss-no-team-banner"
          >
            ×
          </button>
          <p className="text-white font-semibold mb-1">Get started with APL</p>
          <p className="text-slate-400 text-sm mb-3">
            You're registered but haven't started playing yet. IPL 2026 is live — jump in now.
          </p>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/my-team"
              className="bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-600 transition-colors"
              data-testid="no-team-build-btn"
            >
              Build season team
            </Link>
            <Link
              to="/predictor"
              className="bg-slate-700 text-white px-4 py-2 rounded-lg text-sm hover:bg-slate-600 transition-colors"
              data-testid="no-team-predict-btn"
            >
              Make a prediction
            </Link>
            <Link
              to="/daily"
              className="bg-slate-700 text-white px-4 py-2 rounded-lg text-sm hover:bg-slate-600 transition-colors"
              data-testid="no-team-daily-btn"
            >
              Pick Match Day XI
            </Link>
          </div>
        </div>
      )}

      {/* 1. HEADER - Title and Logo */}
      <div className="flex items-center gap-3 mb-4">
        <img src="/logo.png" alt="APL" className="w-12 h-12 rounded-xl shadow-lg" />
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">Apna Premier League</h1>
          <p className="text-slate-400 text-xs">Fantasy Cricket</p>
        </div>
        {user && (
          <Link 
            to="/my-team" 
            className="px-3 py-1.5 bg-emerald-500 text-white text-xs font-semibold rounded-lg hover:bg-emerald-600 transition-all flex items-center gap-1"
          >
            <Shield size={12} />
            {userTeam ? "My Team" : "Create Team"}
          </Link>
        )}
      </div>

      {/* APP DOWNLOAD BANNER - web only, between header and stats */}
      {!isNativePlatform && (
        <div className="mb-4 rounded-xl border border-emerald-500/20 bg-gradient-to-r from-emerald-500/10 via-slate-800/80 to-cyan-500/10 p-3" data-testid="app-download-banner">
          <p className="text-white font-semibold text-sm text-center mb-2">Get the APL App</p>
          <p className="text-slate-400 text-[11px] text-center mb-3">Better experience. More features. Build your squad on the go.</p>
          <div className="flex gap-2">
            <a
              href="https://apps.apple.com/app/id6760789788"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 bg-slate-900 border border-slate-600 text-white px-3 py-2.5 rounded-lg text-xs font-semibold hover:bg-slate-700 transition-colors"
              data-testid="homepage-download-ios-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              App Store
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.apnapremierleague.app"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-3 py-2.5 rounded-lg text-xs font-semibold hover:opacity-90 transition-opacity"
              data-testid="homepage-download-android-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg>
              Google Play
            </a>
          </div>
        </div>
      )}

      {/* 2. STATS BOXES - Clickable */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        {[
          { label: "Matches", value: stats.matches, path: "/matches" },
          { label: "Players", value: stats.players, path: "/players" },
          { label: "Users", value: stats.users, path: "/leaderboard" },
          { label: "Leagues", value: stats.leagues, path: "/leagues" },
          { label: "My Rank", value: user ? (dbUser?.global_rank ? `#${dbUser.global_rank}` : "-") : "View", path: "/leaderboard" },
        ].map(({ label, value, path }) => (
          <Link
            key={label}
            to={path}
            className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-2.5 text-center hover:border-slate-600 transition-all"
            data-testid={`stat-${label.toLowerCase()}`}
          >
            <p className="text-lg font-bold text-white">{value}</p>
            <p className="text-slate-400 text-[10px]">{label}</p>
          </Link>
        ))}
      </div>

      {/* 3. COUNTDOWN TIMER */}
      {nextMatch && (
        <div className="mb-4">
          <CountdownTimer targetDate={nextMatch.date} matchInfo={nextMatch.info} />
        </div>
      )}

      {/* 4. MATCH DAY BANNER - NON-DISMISSABLE (always show if matches available) */}
      <div className="relative overflow-hidden rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-purple-500/10 p-4 mb-4" data-testid="matchday-announcement">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-amber-400">Match Day Mode</h3>
            <p className="text-xs text-slate-300 mt-1 leading-relaxed">
              Pick your best XI for each match and compete daily! No transfer window needed.
            </p>
            <Link
              to="/daily"
              className="inline-flex items-center gap-1.5 mt-2.5 px-4 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-semibold hover:bg-amber-500/25 transition-colors"
              data-testid="try-matchday-btn"
            >
              <Zap className="w-3.5 h-3.5" /> Try Match Day
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* 5. DAILY STREAK */}
      {user && <DailyReward />}

      {/* 6. UPCOMING MATCHES */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-white">Upcoming Matches</h2>
          <Link to="/matches" className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1">
            View All <ChevronRight size={14} />
          </Link>
        </div>
        <div className="space-y-2">
          {upcomingMatches.slice(0, 5).map((match) => (
            <MatchCard key={match.match_id} match={match} compact hasPredictions={matchesWithPredictions.includes(match.match_id)} />
          ))}
          {upcomingMatches.length === 0 && (
            <div className="text-center py-6 text-slate-500 text-sm">
              No upcoming matches scheduled
            </div>
          )}
        </div>
      </div>

      </>)}
    </div>
    </div>
  );
};

// Match Card Component
const MatchCard = ({ match, compact = false, hasPredictions = false }) => {
  const team1Color = getTeamColor(match.team1);
  const team2Color = getTeamColor(match.team2);
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (hasPredictions) {
      navigate("/predictor");
    }
  };

  // Use city names for display
  const team1Display = getDisplayName(match.team1);
  const team2Display = getDisplayName(match.team2);
  const team1ShortDisplay = getDisplayShortName(match.team1_short);
  const team2ShortDisplay = getDisplayShortName(match.team2_short);

  // Extract match number for sorting display
  const matchLabel = match.match_number || match.match_type || "";

  return (
    <div
      data-testid={`match-card-${match.match_id}`}
      onClick={handleCardClick}
      className={`bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl overflow-hidden hover:border-slate-600 transition-all ${
        compact ? "p-3" : "p-6"
      } ${hasPredictions ? "cursor-pointer hover:border-red-500/50 hover:bg-slate-800/70" : ""}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
            {match.status || "upcoming"}
          </span>
          {hasPredictions && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 animate-pulse" data-testid={`prediction-badge-${match.match_id}`}>
              Predictions Open
            </span>
          )}
          {(match.status === "abandoned" || match.status === "no_result") && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400" data-testid={`match-status-badge-${match.match_id}`}>
              {match.status === "abandoned" ? "Abandoned" : "No Result"}
            </span>
          )}
        </div>
        <span className="text-xs text-slate-500">{matchLabel}</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${team1Color.bg} flex items-center justify-center text-white font-bold text-xs`}>
            {team1ShortDisplay}
          </div>
          <span className="font-medium text-white text-sm">{team1Display}</span>
        </div>

        <span className="text-slate-500 text-sm">vs</span>

        <div className="flex items-center gap-2">
          <span className="font-medium text-white text-sm">{team2Display}</span>
          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${team2Color.bg} flex items-center justify-center text-white font-bold text-xs`}>
            {team2ShortDisplay}
          </div>
        </div>
      </div>

      {/* Sponsor badge */}
      {match.sponsor && match.sponsor.name && (
        <div className="mt-2 flex items-center gap-1 text-xs text-slate-500" data-testid={`match-sponsor-${match.match_id}`}>
          <span>Sponsored by</span>
          {match.sponsor.logo_url && (
            <img src={match.sponsor.logo_url} alt={match.sponsor.name} className="h-3 max-w-[40px] object-contain" />
          )}
          <span className="text-slate-400 font-medium">{match.sponsor.name}</span>
        </div>
      )}

      {!compact && (
        <div className="mt-3 pt-3 border-t border-slate-700/50 flex flex-wrap gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1"><Calendar size={12} />{match.date}</span>
          <span className="flex items-center gap-1"><Clock size={12} />{match.time_local || match.time_gmt}</span>
          <span className="flex items-center gap-1"><MapPin size={12} />{match.venue}</span>
        </div>
      )}
    </div>
  );
};

// Matches Page
const MatchesPage = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/matches`);
      // Sort matches by extracting numeric part from match_number
      const sortedMatches = res.data.matches.sort((a, b) => {
        const numA = parseInt((a.match_number || "").match(/\d+/)?.[0] || "999");
        const numB = parseInt((b.match_number || "").match(/\d+/)?.[0] || "999");
        return numA - numB;
      });
      setMatches(sortedMatches);
    } catch (e) {
      console.error("Failed to fetch matches:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }

  return (
    <div>
    <div className="space-y-6" data-testid="matches-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">All {matches.length} Matches</h1>
      </div>
      <div className="space-y-3">
        {matches.map((match) => (
          <MatchCard key={match.match_id} match={match} />
        ))}
      </div>
    </div>
    </div>
  );
};

// Players Page with Search/Filter
const PlayersPage = () => {
  const { dbUser } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("name"); // "name", "points", "credits", "matches"
  
  // Stats gating state for web users (like TeamBuilderPage)
  const [statsLookupCount, setStatsLookupCount] = useState(0);
  const [statsGatedPlayerId, setStatsGatedPlayerId] = useState(null);
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  
  // Check if on native platform
  useEffect(() => {
    const checkPlatform = async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        setIsNativePlatform(Capacitor.isNativePlatform());
      } catch {
        setIsNativePlatform(false);
      }
    };
    checkPlatform();
  }, []);

  // Get stats lookup limit based on tier
  const getStatsLookupLimit = () => {
    const tier = dbUser?.tier || dbUser?.subscription_tier || "free";
    if (tier === "pro_max") return 999; // Unlimited
    if (tier === "pro_plus") return 10;
    if (tier === "pro") return 5;
    return 2; // free
  };

  const fetchPlayers = useCallback(async () => {
    try {
      // Only show players from teams still in the tournament
      const res = await axios.get(`${API}/players?active_only=true`);
      setPlayers(res.data.players);
    } catch (e) {
      console.error("Failed to fetch players:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const countries = [...new Set(players.map(p => p.country))].sort();

  // Player detail overlay
  const [selectedPlayerDetail, setSelectedPlayerDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const openPlayerDetail = async (playerId) => {
    // Check stats lookup limit for web users
    const limit = getStatsLookupLimit();
    if (statsLookupCount >= limit && !isNativePlatform) {
      setStatsGatedPlayerId(playerId);
      return;
    }
    
    setLoadingDetail(true);
    setStatsLookupCount(prev => prev + 1);
    try {
      const res = await axios.get(`${API}/players/${playerId}/detail`);
      setSelectedPlayerDetail(res.data);
    } catch (e) {
      console.error("Failed to load player detail:", e);
    } finally {
      setLoadingDetail(false);
    }
  };

  const filteredPlayers = players.filter(player => {
    const matchesSearch = searchQuery === "" || 
      player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      player.country.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "" || player.role === roleFilter;
    const matchesCountry = countryFilter === "" || player.country === countryFilter;
    return matchesSearch && matchesRole && matchesCountry;
  }).sort((a, b) => {
    switch (sortBy) {
      case "points": return (b.total_points || 0) - (a.total_points || 0);
      case "credits": return (b.credits || 0) - (a.credits || 0);
      case "matches": return (b.matches_played || 0) - (a.matches_played || 0);
      default: return a.name.localeCompare(b.name);
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }

  return (
    <div>
    <div className="space-y-6" data-testid="players-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">All {players.length} Players</h1>
      </div>

      {/* Search & Filters */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search players..."
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
              data-testid="player-search"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg border transition-all flex items-center gap-2 ${
              showFilters ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-slate-800 border-slate-700 text-slate-400'
            }`}
            data-testid="filter-toggle"
          >
            <Filter size={18} />
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-3 gap-3 bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Role</label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                data-testid="role-filter"
              >
                <option value="">All</option>
                <option value="Wicketkeeper">WK</option>
                <option value="Batsman">BAT</option>
                <option value="All-Rounder">AR</option>
                <option value="Bowler">BOWL</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Country</label>
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                data-testid="country-filter"
              >
                <option value="">All</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                data-testid="sort-filter"
              >
                <option value="name">Name (A-Z)</option>
                <option value="points">Points (High-Low)</option>
                <option value="credits">Crore (High-Low)</option>
                <option value="matches">Matches Played</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <p className="text-sm text-slate-400">{filteredPlayers.length} players</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filteredPlayers.map((player) => (
          <div 
            key={player.id} 
            className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 cursor-pointer hover:border-emerald-500/50 transition-colors" 
            onClick={() => openPlayerDetail(player.id)}
            data-testid={`player-card-${player.id}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getTeamColor(player.country).bg} flex items-center justify-center text-white font-bold text-xs`}>
                {player.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <h3 className="font-semibold text-white truncate">{player.name}</h3>
                  {player.is_benched && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 border border-amber-500/40 rounded-full flex-shrink-0" title={`Missed ${player.country_matches - player.matches_played} match(es) while ${player.country} played`}>
                      BENCHED
                    </span>
                  )}
                  {player.caps && player.caps.length > 0 && (
                    <div className="flex gap-0.5 flex-shrink-0">
                      {player.caps.filter(c => c.type === "orange").map((c, i) => (
                        <span key={`o${i}`} className="text-[10px]" title={`Orange Cap Week ${c.week}`}>🟠</span>
                      ))}
                      {player.caps.filter(c => c.type === "purple").map((c, i) => (
                        <span key={`p${i}`} className="text-[10px]" title={`Purple Cap Week ${c.week}`}>🟣</span>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-slate-400 text-sm">{player.country}</p>
              </div>
              {(player.total_points > 0 || player.matches_played > 0) && (
                <div className="text-right">
                  <p className="text-lg font-bold text-emerald-400">{player.total_points || 0}</p>
                  <p className="text-[10px] text-slate-500">pts</p>
                </div>
              )}
            </div>
            
            {/* Matches played badge */}
            {player.matches_played > 0 && (
              <div className="flex items-center gap-1 mb-2">
                <span className="text-xs bg-slate-700/80 text-slate-300 px-2 py-0.5 rounded">
                  {player.matches_played} match{player.matches_played > 1 ? 'es' : ''}
                </span>
                {player.matches_played > 0 && player.total_points > 0 && (
                  <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded">
                    avg {(player.total_points / player.matches_played).toFixed(1)}
                  </span>
                )}
              </div>
            )}
            
            {/* Player Stats Row */}
            {(player.total_runs > 0 || player.total_wickets > 0 || player.total_catches > 0 || player.total_stumpings > 0 || player.total_run_outs > 0) && (
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {player.total_runs > 0 && (
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                    {player.total_runs} runs
                  </span>
                )}
                {player.total_fours > 0 && (
                  <span className="text-xs bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded">
                    {player.total_fours} 4s
                  </span>
                )}
                {player.total_sixes > 0 && (
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                    {player.total_sixes} 6s
                  </span>
                )}
                {player.total_wickets > 0 && (
                  <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                    {player.total_wickets} wkts
                  </span>
                )}
                {player.total_catches > 0 && (
                  <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                    {player.total_catches} ct
                  </span>
                )}
                {player.total_stumpings > 0 && (
                  <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-0.5 rounded">
                    {player.total_stumpings} st
                  </span>
                )}
                {player.total_run_outs > 0 && (
                  <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">
                    {player.total_run_outs} ro
                  </span>
                )}
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${ROLE_COLORS[player.role]}`}>
                {player.role}
              </span>
              <span className="text-emerald-400 font-mono font-semibold">
                {player.credits || player.base_credits} Cr
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Player Detail Overlay */}
      {(selectedPlayerDetail || loadingDetail) && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
          onClick={() => setSelectedPlayerDetail(null)}
        >
          <div 
            className="bg-slate-800 border border-slate-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {loadingDetail && !selectedPlayerDetail ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="animate-spin text-emerald-400" size={32} />
              </div>
            ) : selectedPlayerDetail && (
              <>
                <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getTeamColor(selectedPlayerDetail.player.country).bg} flex items-center justify-center text-white font-bold text-sm`}>
                      {selectedPlayerDetail.player.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">{selectedPlayerDetail.player.name}</h3>
                      <p className="text-slate-400 text-sm">{selectedPlayerDetail.player.country} • {selectedPlayerDetail.player.role}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedPlayerDetail(null)} className="text-slate-400 hover:text-white p-1"><X size={22} /></button>
                </div>
                <div className="p-4 border-b border-slate-700/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-400 text-sm font-medium">Player Value</span>
                    <span className="text-emerald-400 font-bold text-2xl">{selectedPlayerDetail.credits} Crore</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm"><span className="text-slate-400">Base</span><span className="text-white">{selectedPlayerDetail.credit_breakdown.base}</span></div>
                    {selectedPlayerDetail.stats.matches_played > 0 && (<>
                      <div className="flex justify-between text-sm"><span className="text-slate-400">Avg Points/Match</span><span className="text-cyan-400">{selectedPlayerDetail.credit_breakdown.avg_points_per_match}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-slate-400">Performance Bonus</span><span className="text-amber-400">+{selectedPlayerDetail.credit_breakdown.performance_bonus}</span></div>
                      {selectedPlayerDetail.credit_breakdown.cap_bonus > 0 && (
                        <div className="flex justify-between text-sm"><span className="text-slate-400">Cap Bonus</span><span className="text-purple-400">+{selectedPlayerDetail.credit_breakdown.cap_bonus}</span></div>
                      )}
                      <div className="border-t border-slate-700 pt-2 flex justify-between text-sm"><span className="text-slate-400">Final Value</span><span className="text-emerald-400 font-bold">{selectedPlayerDetail.credit_breakdown.final_credits} Crore</span></div>
                    </>)}
                    <p className="text-[10px] text-slate-500 mt-1">{selectedPlayerDetail.credit_breakdown.formula}</p>
                  </div>
                </div>
                {/* Caps Section */}
                {selectedPlayerDetail.caps && selectedPlayerDetail.caps.length > 0 && (
                  <div className="px-4 pb-2 pt-2 border-b border-slate-700/50">
                    <h4 className="text-slate-400 text-sm font-medium mb-2">Awards</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedPlayerDetail.caps.map((cap, i) => (
                        <span key={i} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${
                          cap.type === "orange" 
                            ? "bg-orange-500/20 text-orange-400 border border-orange-500/30" 
                            : "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                        }`}>
                          {cap.type === "orange" ? "🟠" : "🟣"} 
                          {cap.type === "orange" ? "Orange" : "Purple"} Cap - Week {cap.week}
                          {cap.runs && ` (${cap.runs} runs)`}
                          {cap.wickets && ` (${cap.wickets} wkts)`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="p-4 border-b border-slate-700/50">
                  <h4 className="text-slate-400 text-sm font-medium mb-3">Tournament Stats</h4>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: "Matches", value: selectedPlayerDetail.stats.matches_played, color: "text-white" },
                      { label: "Points", value: selectedPlayerDetail.stats.total_points, color: "text-emerald-400" },
                      { label: "Avg Pts", value: selectedPlayerDetail.stats.avg_points, color: "text-cyan-400" },
                      { label: "Runs", value: selectedPlayerDetail.stats.total_runs, color: "text-white" },
                      { label: "4s", value: selectedPlayerDetail.stats.total_fours, color: "text-amber-400" },
                      { label: "6s", value: selectedPlayerDetail.stats.total_sixes, color: "text-amber-400" },
                      { label: "Wickets", value: selectedPlayerDetail.stats.total_wickets, color: "text-red-400" },
                      { label: "Economy", value: selectedPlayerDetail.stats.matches_played > 0 && selectedPlayerDetail.stats.total_wickets >= 0 ? (selectedPlayerDetail.stats.economy_rate || "-") : "-", color: "text-cyan-400" },
                      { label: "Catches", value: selectedPlayerDetail.stats.total_catches, color: "text-blue-400" },
                      { label: "Stumpings", value: selectedPlayerDetail.stats.total_stumpings, color: "text-teal-400" },
                      { label: "Run Outs", value: selectedPlayerDetail.stats.total_run_outs, color: "text-orange-400" },
                    ].map(stat => (
                      <div key={stat.label} className="bg-slate-900/50 rounded-lg p-2 text-center">
                        <p className={`font-bold text-lg ${stat.color}`}>{stat.value}</p>
                        <p className="text-[10px] text-slate-500">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {selectedPlayerDetail.performances.length > 0 && (
                  <div className="p-4">
                    <h4 className="text-slate-400 text-sm font-medium mb-3">Match Performances</h4>
                    <div className="space-y-2">
                      {selectedPlayerDetail.performances.map((perf, idx) => (
                        <div key={idx} className="bg-slate-900/50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white text-sm font-medium">{perf.match_label || `Match ${perf.match_id}`}</span>
                            <span className="text-emerald-400 font-bold">{perf.points} pts</span>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
                            {perf.runs > 0 && <span>{perf.runs} runs</span>}
                            {perf.fours > 0 && <span>{perf.fours} fours</span>}
                            {perf.sixes > 0 && <span>{perf.sixes} sixes</span>}
                            {perf.wickets > 0 && <span>{perf.wickets} wkts</span>}
                            {perf.catches > 0 && <span>{perf.catches} ct</span>}
                            {perf.stumpings > 0 && <span>{perf.stumpings} st</span>}
                            {perf.run_outs > 0 && <span>{perf.run_outs} ro</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Stats Gated Modal for Web Users */}
      {statsGatedPlayerId && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-sm p-6 text-center">
            <div className="w-16 h-16 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center mb-4">
              <Shield className="text-amber-400" size={32} />
            </div>
            <h3 className="text-white font-bold text-lg mb-2">Stats Limit Reached</h3>
            <p className="text-slate-400 text-sm mb-4">
              You've used your {getStatsLookupLimit()} free player stat lookup{getStatsLookupLimit() > 1 ? 's' : ''} for this session.
              {isNativePlatform 
                ? " Watch a short video to unlock 5 more player stats."
                : " Download the APL app for unlimited player stats."}
            </p>
            <div className="space-y-2">
              {/* Rewarded video button — native iOS only */}
              {isNativePlatform && (
                <button
                  onClick={async () => {
                    try {
                      const { waitForAdMob } = await import("./utils/adMobState");
                      const status = await waitForAdMob();
                      if (status.failed) {
                        console.error("AdMob not ready — cannot show rewarded video");
                        return;
                      }
                      const { AdMob, RewardAdPluginEvents } = await import("@capacitor-community/admob");
                      const { getRewardedVideoId } = await import("../utils/adConfig");
                      const adId = await getRewardedVideoId();
                      await AdMob.prepareRewardVideoAd({ adId });
                      const rewarded = await new Promise((resolve) => {
                        AdMob.addListener(RewardAdPluginEvents.Rewarded, () => resolve(true));
                        AdMob.addListener(RewardAdPluginEvents.Dismissed, () => resolve(false));
                        AdMob.showRewardVideoAd().catch(() => resolve(false));
                      });
                      if (!rewarded) return;
                      // Grant 5 additional lookups
                      setStatsLookupCount(prev => Math.max(0, prev - 5));
                      setStatsGatedPlayerId(null);
                      // Now open the player that was gated
                      if (statsGatedPlayerId) {
                        setLoadingDetail(true);
                        try {
                          const res = await axios.get(`${API}/players/${statsGatedPlayerId}/detail`);
                          setSelectedPlayerDetail(res.data);
                        } catch (e) {
                          console.error("Failed to load player detail:", e);
                        } finally {
                          setLoadingDetail(false);
                        }
                      }
                    } catch (e) {
                      console.error("AdMob rewarded error:", e);
                    }
                  }}
                  className="w-full px-4 py-3 bg-amber-500/20 border border-amber-500/30 text-amber-400 rounded-lg font-bold hover:bg-amber-500/30 transition-all flex items-center justify-center gap-2"
                  data-testid="rewarded-video-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  Watch video — unlock 5 stats
                </button>
              )}
              {!isNativePlatform && (
                <>
                  <a
                    href="https://apps.apple.com/app/apl-fantasy-cricket"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg font-bold hover:bg-slate-700 transition-all flex items-center justify-center gap-2 border border-slate-600"
                    data-testid="download-ios-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    Download on App Store
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.apnapremierleague.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2"
                    data-testid="download-android-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                    Google Play
                  </a>
                </>
              )}
              <button
                onClick={() => setStatsGatedPlayerId(null)}
                className="w-full px-4 py-2 text-slate-400 hover:text-white transition-all"
              >
                Cancel
              </button>
            </div>
            <p className="text-slate-500 text-xs mt-4">
              {isNativePlatform 
                ? "Upgrade to Pro for more lookups or Pro Max for unlimited stats"
                : "Get the APL app for rewarded video unlocks and offline access"}
            </p>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

// Profile Page with Simple WhatsApp Links and Referral Code Input
const ProfilePage = () => {
  const { user, dbUser, setDbUser, logout } = useAuth();
  const { sport } = useSport();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [userTeam, setUserTeam] = useState(null);
  const [referralInput, setReferralInput] = useState("");
  const [applyingReferral, setApplyingReferral] = useState(false);
  const [referralError, setReferralError] = useState("");
  const [referralSuccess, setReferralSuccess] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [referralStatus, setReferralStatus] = useState(null);
  const [budgetInfo, setBudgetInfo] = useState(null);
  const fileInputRef = useRef(null);
  // Team name editing
  const [editingTeamName, setEditingTeamName] = useState(false);
  const [teamNameInput, setTeamNameInput] = useState("");
  const [teamNameError, setTeamNameError] = useState("");
  const [savingTeamName, setSavingTeamName] = useState(false);
  // Account deletion
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          const token = await user.getIdToken();
          const [teamsRes, referralRes, budgetRes] = await Promise.all([
            axios.get(`${API}/teams/my-teams`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`${API}/referrals/weekly-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
            axios.get(`${API}/budget/my-budget`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
          ]);
          
          if (teamsRes.data.teams && teamsRes.data.teams.length > 0) {
            setUserTeam(teamsRes.data.teams[0]);
            setTeamNameInput(teamsRes.data.teams[0].name || "");
          }
          
          if (referralRes && referralRes.data) {
            setReferralStatus(referralRes.data);
          }
          
          if (budgetRes && budgetRes.data) {
            setBudgetInfo(budgetRes.data);
          }
        } catch (e) {
          console.error("Failed to fetch user data:", e);
        }
      }
    };
    fetchUserData();
  }, [user]);

  // Handle team name save
  const handleSaveTeamName = async () => {
    if (!teamNameInput.trim() || teamNameInput.trim().length < 2) {
      setTeamNameError("Team name must be at least 2 characters");
      return;
    }
    if (teamNameInput.trim().length > 30) {
      setTeamNameError("Team name must be less than 30 characters");
      return;
    }
    
    setSavingTeamName(true);
    setTeamNameError("");
    try {
      const token = await user.getIdToken();
      await axios.put(
        `${API}/teams/${userTeam.id}/name`,
        { name: teamNameInput.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserTeam({ ...userTeam, name: teamNameInput.trim(), name_locked: true });
      setEditingTeamName(false);
    } catch (e) {
      setTeamNameError(e.response?.data?.detail || "Failed to update team name");
    } finally {
      setSavingTeamName(false);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setPhotoError("Please upload a JPEG, PNG, WebP, or GIF image.");
      return;
    }

    // Validate file size (500KB max)
    if (file.size > 500 * 1024) {
      setPhotoError("Image too large. Max 500KB.");
      return;
    }

    setUploadingPhoto(true);
    setPhotoError("");

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64 = event.target?.result;
          const token = await user.getIdToken();
          await axios.post(
            `${API}/auth/upload-photo`,
            { photo_data: base64 },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Update local state
          if (setDbUser && dbUser) {
            setDbUser({ ...dbUser, photo_url: base64 });
          }
        } catch (err) {
          setPhotoError(err.response?.data?.detail || "Failed to upload photo");
        } finally {
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setPhotoError("Failed to process image");
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    try {
      const token = await user.getIdToken();
      await axios.delete(`${API}/auth/remove-photo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (setDbUser && dbUser) {
        setDbUser({ ...dbUser, photo_url: null });
      }
    } catch (err) {
      setPhotoError("Failed to remove photo");
    }
  };

  // Simple WhatsApp share link
  const shareWhatsApp = () => {
    const code = dbUser?.referral_code || 'APL-XXXXXX';
    const message = `Join Apna Premier League! 🏏

Use my referral code: *${code}*
I earn 25 pts + 1 Cr when you join!

${window.location.origin}/auth?ref=${code}`;
    
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const copyCode = async () => {
    const code = dbUser?.referral_code || '';
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Show the code in an alert as last resort
      alert(`Your referral code: ${code}`);
    }
  };

  const applyReferralCode = async () => {
    if (!referralInput.trim()) return;
    
    setApplyingReferral(true);
    setReferralError("");
    setReferralSuccess("");
    
    try {
      const token = await user.getIdToken();
      // Use V2 endpoint which gives both points AND crores
      const res = await axios.post(
        `${API}/referrals/apply-v2?referral_code=${referralInput.trim().toUpperCase()}&signup_source_sport=${sport}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      const ptsMsg = data.referrer_earned?.points > 0 ? `${data.referrer_earned.points} pts + ` : '';
      const crMsg = data.referrer_earned?.crores > 0 ? `${data.referrer_earned.crores} Cr` : 'Cr cap reached';
      setReferralSuccess(`Referral applied! Your friend earned ${ptsMsg}${crMsg}. You got +${data.your_bonus?.budget_crores || 5} Cr budget bonus!`);
      setReferralInput("");
      // Update dbUser to reflect the change
      if (setDbUser && dbUser) {
        setDbUser({ ...dbUser, referred_by: referralInput.trim().toUpperCase() });
      }
    } catch (e) {
      setReferralError(e.response?.data?.detail || "Failed to apply referral code");
    } finally {
      setApplyingReferral(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      console.error("Logout failed:", e);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    setDeleteError("");
    try {
      const token = await user.getIdToken();
      await axios.delete(`${API}/auth/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await logout();
      navigate("/auth", { replace: true });
    } catch (e) {
      setDeleteError(e.response?.data?.detail || "Failed to delete account. Please try again.");
      setDeletingAccount(false);
    }
  };

  // Redirect to auth if no user
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const hasAppliedReferral = dbUser?.referred_by;
  const hasCustomPhoto = dbUser?.photo_url && dbUser.photo_url.startsWith('data:image');

  return (
    <div className="space-y-6" data-testid="profile-page">
      <h1 className="text-2xl font-bold text-white">Profile</h1>

      <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-4 mb-6">
          {/* Profile Photo with Upload */}
          <div className="relative">
            {hasCustomPhoto ? (
              <img 
                src={dbUser.photo_url} 
                alt="Profile" 
                className="w-16 h-16 rounded-full object-cover border-2 border-emerald-500"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold text-xl">
                {(dbUser?.display_name || user.email)?.[0]?.toUpperCase()}
              </div>
            )}
            {/* Upload/Remove buttons */}
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handlePhotoUpload} 
              accept="image/jpeg,image/png,image/webp,image/gif" 
              className="hidden" 
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute -bottom-1 -right-1 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center text-white hover:bg-emerald-600 transition-all border-2 border-slate-800"
              data-testid="upload-photo-btn"
            >
              {uploadingPhoto ? <Loader2 className="animate-spin" size={14} /> : <Camera size={14} />}
            </button>
            {hasCustomPhoto && (
              <button
                onClick={removePhoto}
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-all"
                data-testid="remove-photo-btn"
              >
                <X size={10} />
              </button>
            )}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white" data-testid="profile-display-name">{userTeam?.name || dbUser?.preferred_team_name || dbUser?.display_name || "User"}</h2>
            <p className="text-slate-400 text-sm">{user.email}</p>
            {sport !== "football" && userTeam && (
              <div className="mt-1">
                {userTeam.name_locked || !editingTeamName ? (
                  <div className="flex items-center gap-2">
                    <p className="text-emerald-400 text-sm">Team: {userTeam.name}</p>
                    {!userTeam.name_locked && (
                      <button
                        onClick={() => setEditingTeamName(true)}
                        className="text-xs text-slate-400 hover:text-white"
                        data-testid="edit-team-name-btn"
                      >
                        (Edit)
                      </button>
                    )}
                    {userTeam.name_locked && (
                      <Lock size={12} className="text-slate-500" />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="text"
                      value={teamNameInput}
                      onChange={(e) => setTeamNameInput(e.target.value)}
                      className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm w-32"
                      placeholder="Team name"
                      maxLength={30}
                      data-testid="team-name-input"
                    />
                    <button
                      onClick={handleSaveTeamName}
                      disabled={savingTeamName}
                      className="px-2 py-1 bg-emerald-500 text-white text-xs rounded hover:bg-emerald-600 disabled:opacity-50"
                      data-testid="save-team-name-btn"
                    >
                      {savingTeamName ? "..." : "Save"}
                    </button>
                    <button
                      onClick={() => { setEditingTeamName(false); setTeamNameInput(userTeam.name); }}
                      className="text-xs text-slate-400 hover:text-white"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {teamNameError && <p className="text-red-400 text-xs mt-1">{teamNameError}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Photo upload error */}
        {photoError && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg p-3 mb-4 text-sm">
            {photoError}
          </div>
        )}

        {sport === "football" && <FootballProfileStats />}
        {sport === "football" && <FootballReferralPanel />}

        {sport !== "football" && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-slate-900/50 rounded-lg p-4 text-center">
            <p className="text-slate-400 text-sm mb-1">Total Points</p>
            <p className="text-2xl font-bold text-emerald-400">{dbUser?.total_points || 0}</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4 text-center">
            <p className="text-slate-400 text-sm mb-1">Referrals</p>
            <p className="text-2xl font-bold text-cyan-400">{dbUser?.referral_count || 0}/3</p>
          </div>
        </div>
        )}
        
        {/* Budget & Crore Earnings Card */}
        {sport !== "football" && (budgetInfo || referralStatus) && (
          <div className="bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 rounded-xl p-4 mb-6">
            <h3 className="text-md font-semibold text-violet-400 mb-3 flex items-center gap-2">
              <Trophy size={18} />
              {budgetInfo?.current_week ? `Week ${budgetInfo.current_week} Status` : "Pre-Season Status"}
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {budgetInfo && (
                <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-cyan-400">{budgetInfo.budget || 0}</p>
                  <p className="text-[10px] text-slate-400">Budget (Cr)</p>
                </div>
              )}
              {referralStatus && (
                <>
                  <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-emerald-400">{referralStatus.crores_earned_this_week || 0}</p>
                    <p className="text-[10px] text-slate-400">Cr Earned (Week)</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-amber-400">{referralStatus.crores_remaining || 0}</p>
                    <p className="text-[10px] text-slate-400">Cr Remaining</p>
                  </div>
                </>
              )}
            </div>
            {referralStatus && (
              <p className="text-xs text-slate-400 mt-2">
                Earn +25 pts + 1 Cr (cricket) <span className="text-amber-400">+50 football pts</span> per referral (max 3/week, resets each week). New users get +5 Cr with your code!
              </p>
            )}
          </div>
        )}

        {/* Apply Referral Code Section - Only show if user hasn't applied one */}
        {sport !== "football" && !hasAppliedReferral && (
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-6">
            <h3 className="text-md font-semibold text-purple-400 mb-3">🎫 Have a Referral Code?</h3>
            
            {referralError && (
              <div className="bg-red-500/20 text-red-400 rounded-lg p-2 mb-3 text-sm">
                {referralError}
              </div>
            )}
            {referralSuccess && (
              <div className="bg-emerald-500/20 text-emerald-400 rounded-lg p-2 mb-3 text-sm">
                {referralSuccess}
              </div>
            )}
            
            <div className="flex gap-2">
              <input
                type="text"
                value={referralInput}
                onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                placeholder="Enter code (APL-XXXXXX)"
                className="flex-1 px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 font-mono"
                data-testid="apply-referral-input"
              />
              <button
                onClick={applyReferralCode}
                disabled={applyingReferral || !referralInput.trim()}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="apply-referral-btn"
              >
                {applyingReferral ? <Loader2 className="animate-spin" size={16} /> : <Gift size={16} />}
                Apply
              </button>
            </div>
            <p className="text-purple-300/70 text-xs mt-2">
              Enter a friend's code to help them earn 25 pts + 1 Cr! You get +5 Cr budget.
            </p>
          </div>
        )}

        {/* Show applied referral if exists */}
        {sport !== "football" && hasAppliedReferral && (
          <div className="bg-slate-900/50 rounded-lg p-3 mb-6">
            <p className="text-slate-400 text-sm">
              ✓ Referral code applied: <span className="text-emerald-400 font-mono">{dbUser?.referred_by}</span>
            </p>
          </div>
        )}

        {/* Share Your Referral Section - cricket only */}
        {sport !== "football" && (
        <div className="bg-gradient-to-br from-emerald-600 to-cyan-600 rounded-xl p-5 mb-6">
          <h3 className="text-lg font-bold text-white mb-3">🎁 Share & Earn 25 Pts + 1 Cr</h3>
          
          <div className="flex items-center gap-2 bg-white/20 rounded-lg p-3 mb-3">
            <span className="font-mono text-lg text-white flex-1">{dbUser?.referral_code || 'APL-XXXXXX'}</span>
            <button 
              onClick={copyCode}
              className={`p-2 rounded-lg transition-all ${copied ? 'bg-emerald-500' : 'bg-white/20 hover:bg-white/30'}`}
              data-testid="copy-referral-btn"
            >
              <Copy className="text-white" size={18} />
            </button>
            <button 
              onClick={shareWhatsApp}
              data-testid="whatsapp-share-btn"
              className="p-2 bg-green-500 hover:bg-green-600 rounded-lg transition-all"
            >
              <Share2 className="text-white" size={18} />
            </button>
          </div>

          <p className="text-white/90 text-sm">
            Share via WhatsApp • Max 3 referrals (75 pts + 3 Cr/week)
          </p>
        </div>
        )}

        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center justify-center gap-2 bg-red-500/20 text-red-400 font-semibold py-3 rounded-lg hover:bg-red-500/30 transition-all"
        >
          <LogOut size={18} /> Logout
        </button>

        <Link to="/privacy" className="flex items-center justify-center gap-2 text-slate-500 hover:text-slate-400 text-sm transition-colors mt-3" data-testid="profile-privacy-link">
          <Shield size={14} /> Privacy Policy
        </Link>

        {/* Upgrade to Pro link */}
        {(!dbUser?.tier || dbUser.tier === "free" || (dbUser.tier_expires_at && new Date(dbUser.tier_expires_at) < new Date())) && (
          <Link to="/subscribe" className="flex items-center justify-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-medium transition-colors mt-2" data-testid="profile-upgrade-link">
            <Crown size={14} /> Upgrade to Pro
          </Link>
        )}

        {/* Replay Tutorial */}
        <button
          onClick={async () => {
            try {
              const token = await user.getIdToken();
              await axios.put(`${API}/users/tutorial-reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
              window.location.reload();
            } catch (e) { console.error(e); }
          }}
          className="flex items-center justify-center gap-2 text-slate-500 hover:text-slate-400 text-sm transition-colors mt-2 w-full"
          data-testid="replay-tutorial-btn"
        >
          <Star size={14} /> Replay Tutorial
        </button>

        {/* Delete Account */}
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="flex items-center justify-center gap-2 text-red-500/60 hover:text-red-400 text-xs transition-colors mt-4 w-full"
          data-testid="delete-account-btn"
        >
          Delete Account
        </button>
      </div>

      {/* Delete Account Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-red-500/30 rounded-2xl p-6 max-w-sm w-full" data-testid="delete-account-dialog">
            <h2 className="text-xl font-bold text-white mb-3">Delete Account?</h2>
            <p className="text-slate-300 text-sm mb-4">
              Are you sure? This will permanently delete your account and all data. This action cannot be undone.
            </p>
            {deleteError && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg p-3 mb-4 text-sm">
                {deleteError}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(""); }}
                className="flex-1 py-3 bg-slate-700 text-white font-medium rounded-lg hover:bg-slate-600 transition-all"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                className="flex-1 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-delete-btn"
              >
                {deletingAccount ? <Loader2 className="animate-spin" size={16} /> : null}
                {deletingAccount ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Points System Page
const PointsSystemPage = () => {
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPoints = async () => {
      try {
        const res = await axios.get(`${API}/points-system`);
        setPoints(res.data);
      } catch (e) {
        console.error("Failed to fetch points:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchPoints();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="points-page">
      <h1 className="text-2xl font-bold text-white">Points System</h1>
      
      {points && (
        <div className="grid gap-4">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-blue-400 mb-3">Batting</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Each Run</span><span className="text-white">{points.batting.run} pt</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Boundary (4)</span><span className="text-white">+{points.batting.boundary_4} pt</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Six</span><span className="text-white">+{points.batting.boundary_6} pts</span></div>
              <div className="flex justify-between"><span className="text-slate-400">30 Runs</span><span className="text-emerald-400">+{points.batting.milestone_30} pts</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Half-Century</span><span className="text-emerald-400">+{points.batting.milestone_50} pts</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Century</span><span className="text-emerald-400">+{points.batting.milestone_100} pts</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Duck Out</span><span className="text-red-400">{points.batting.duck_out} pts</span></div>
            </div>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-red-400 mb-3">Bowling</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">1-2 Wickets</span><span className="text-white">{points.bowling.wicket_1_2} pts each</span></div>
              <div className="flex justify-between"><span className="text-slate-400">3 Wickets</span><span className="text-emerald-400">{points.bowling.wicket_3} pts</span></div>
              <div className="flex justify-between"><span className="text-slate-400">4 Wickets</span><span className="text-emerald-400">{points.bowling.wicket_4} pts</span></div>
              <div className="flex justify-between"><span className="text-slate-400">5 Wickets</span><span className="text-emerald-400">{points.bowling.wicket_5} pts</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Maiden</span><span className="text-white">{points.bowling.maiden} pts</span></div>
              <div className="border-t border-slate-700 mt-2 pt-2">
                <p className="text-xs text-slate-500 mb-2">Economy Rate Bonus/Penalty (min {points.bowling.economy_min_overs} overs)</p>
                <div className="flex justify-between"><span className="text-slate-400">Best (below 6.0)</span><span className="text-emerald-400">+{points.bowling.economy_best} pts</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Good (6.0-7.5)</span><span className="text-emerald-400">+{points.bowling.economy_good} pts</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Okay (7.5-9.0)</span><span className="text-slate-400">{points.bowling.economy_okay} pts</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Bad (9.0-10.5)</span><span className="text-red-400">{points.bowling.economy_bad} pts</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Worst (above 10.5)</span><span className="text-red-400">{points.bowling.economy_worst} pts</span></div>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-amber-400 mb-3">Fielding & Bonus</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Catch</span><span className="text-white">{points.fielding.catch} pts</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Stumping</span><span className="text-white">{points.fielding.stumping} pts</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Starting XI</span><span className="text-white">+{points.other.starting_xi} pts</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Captain</span><span className="text-yellow-400">{points.multipliers.captain}x points</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Vice Captain</span><span className="text-cyan-400">{points.multipliers.vice_captain}x points</span></div>
              <div className="border-t border-slate-700 mt-2 pt-2">
                <p className="text-xs text-slate-500 mb-2">Weekly Cap Awards</p>
                <div className="flex justify-between"><span className="text-orange-400">Orange Cap (Most Runs)</span><span className="text-orange-400">+{points.other.orange_cap} pts</span></div>
                <div className="flex justify-between"><span className="text-purple-400">Purple Cap (Most Wickets)</span><span className="text-purple-400">+{points.other.purple_cap} pts</span></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

import SplashScreen from "./components/SplashScreen";

// Main App
function HomeRouter() {
  const { sport } = useSport();
  return (
    <>
      <WhatsNewModal />
      <SportNavigator />
      {sport === "football" ? <FootballHomePage /> : <HomePage />}
    </>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <AuthProvider>
      <SportProvider>
        <div className="App min-h-screen bg-slate-900">
          {showSplash && <SplashScreen onFinish={() => setShowSplash(false)} />}
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </div>
      </SportProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { user, loading, dbUser, setDbUser, getAuthHeader } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const showNav = location.pathname !== "/auth";
  const [showTutorial, setShowTutorial] = useState(false);
  const tutorialResolved = useRef(false);
  const [showNameUpdateModal, setShowNameUpdateModal] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [nameUpdateLoading, setNameUpdateLoading] = useState(false);
  const [nameUpdateError, setNameUpdateError] = useState("");
  const [isNativePlatform, setIsNativePlatform] = useState(false);

  // Detect native platform
  useEffect(() => {
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        setIsNativePlatform(Capacitor.isNativePlatform());
      } catch { setIsNativePlatform(false); }
    })();
  }, []);

  // Handle Android hardware back button
  useEffect(() => {
    let backListener = null;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor.isNativePlatform()) {
          const { App: CapApp } = await import("@capacitor/app");
          backListener = await CapApp.addListener("backButton", ({ canGoBack }) => {
            if (canGoBack) {
              window.history.back();
            } else {
              CapApp.minimizeApp();
            }
          });
        }
      } catch (e) {
        console.log("Back button handler not available:", e);
      }
    })();
    return () => {
      if (backListener) backListener.remove();
    };
  }, []);

  // Show tutorial for new users who haven't seen it (only once per session —
  // guard prevents re-opening when other setDbUser calls flip dbUser before the
  // backend has_seen_tutorial flag round-trips back).
  useEffect(() => {
    if (user && dbUser && dbUser.has_seen_tutorial === false && !tutorialResolved.current) {
      setShowTutorial(true);
    }
  }, [user, dbUser]);

  // Session tracking (Section 8.5/10): start on foreground, end on background
  useEffect(() => {
    if (!user) return;
    const API = process.env.REACT_APP_BACKEND_URL + "/api";
    let sessionId = "";
    let startedAt = Date.now();
    let listener = null;
    const start = async () => {
      startedAt = Date.now();
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      try {
        const { Capacitor } = await import("@capacitor/core");
        await axios.post(`${API}/sessions/start`, { session_id: sessionId, platform: Capacitor.getPlatform() }, { headers: getAuthHeader() });
      } catch (e) { /* best effort */ }
    };
    const end = async () => {
      if (!sessionId) return;
      const dur = Math.round((Date.now() - startedAt) / 1000);
      try { await axios.post(`${API}/sessions/end`, { session_id: sessionId, duration_seconds: dur }, { headers: getAuthHeader() }); } catch (e) {}
    };
    start();
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor.isNativePlatform()) {
          const { App: CapApp } = await import("@capacitor/app");
          listener = await CapApp.addListener("appStateChange", ({ isActive }) => { isActive ? start() : end(); });
        }
      } catch (e) {}
    })();
    const onVis = () => { document.hidden ? end() : start(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      end();
      document.removeEventListener("visibilitychange", onVis);
      if (listener) listener.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Show name update modal for users with email as display_name
  useEffect(() => {
    if (user && dbUser && dbUser.display_name && dbUser.display_name.includes("@") && !dbUser.name_update_prompted) {
      setNewDisplayName("");
      setShowNameUpdateModal(true);
    }
  }, [user, dbUser]);

  const handleNameUpdate = async () => {
    if (!newDisplayName.trim() || newDisplayName.trim().length < 2) {
      setNameUpdateError("Name must be at least 2 characters");
      return;
    }
    
    setNameUpdateLoading(true);
    setNameUpdateError("");
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const res = await axios.put(`${API}/users/update-name`, {
        display_name: newDisplayName.trim()
      }, { headers: getAuthHeader() });
      
      // Update local state
      setDbUser({ ...dbUser, display_name: newDisplayName.trim(), name_update_prompted: true });
      setShowNameUpdateModal(false);
    } catch (err) {
      setNameUpdateError(err.response?.data?.detail || "Failed to update name");
    } finally {
      setNameUpdateLoading(false);
    }
  };

  // Register for push notifications when user is authenticated
  useEffect(() => {
    if (user) {
      registerPushNotifications(() => user.getIdToken());
    }
  }, [user]);

  // Universal Links (iOS) / App Links (Android): route deep links into the app
  useEffect(() => {
    let cleanup;
    setupDeepLinkHandler(navigate).then((fn) => { cleanup = fn; });
    return () => { cleanup?.(); };
  }, [navigate]);

  // Re-register push token on app foreground (FCM tokens rotate)
  useEffect(() => {
    if (!user) return;
    let listener;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import("@capacitor/app");
        listener = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) {
            registerPushNotifications(() => user.getIdToken());
          }
        });
      } catch (e) {
        console.log("App state listener failed:", e);
      }
    })();
    return () => { listener?.remove(); };
  }, [user]);

  // Initialize AdMob on native platforms (ATT is handled natively in AppDelegate.swift)
  useEffect(() => {
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor.isNativePlatform()) {
          const platform = Capacitor.getPlatform();
          if (platform === "ios") {
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
          const { AdMob } = await import("@capacitor-community/admob");
          const { getAppId } = await import("./utils/adConfig");
          const appId = await getAppId();
          await AdMob.initialize({
            requestTrackingAuthorization: false,
            ...(appId ? { initializeForTesting: false } : {}),
          });
          setAdMobReady();
          console.log("AdMob: initialized successfully");
        }
      } catch (e) {
        setAdMobFailed();
        console.error("AdMob: initialization failed", e);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }

  return (
    <>
      <Toaster position="bottom-center" offset="calc(4.75rem + env(safe-area-inset-bottom, 0px))" mobileOffset="calc(4.75rem + env(safe-area-inset-bottom, 0px))" />
      {/* Name Update Modal for users with email as display_name */}
      {showNameUpdateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-2">Update Your Name</h2>
            <p className="text-slate-400 text-sm mb-4">
              Please enter your name to continue. This will be shown on leaderboards and to other users.
            </p>
            
            {nameUpdateError && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg p-3 mb-4 text-sm">
                {nameUpdateError}
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm text-slate-400 mb-1">Your Name *</label>
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="Enter your name"
                required
                minLength={2}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                data-testid="name-update-input"
              />
            </div>
            
            <button
              onClick={handleNameUpdate}
              disabled={nameUpdateLoading}
              className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="name-update-submit"
            >
              {nameUpdateLoading ? <Loader2 className="animate-spin" size={20} /> : null}
              {nameUpdateLoading ? "Updating..." : "Update Name"}
            </button>
          </div>
        </div>
      )}

      {showTutorial && <GameplayTutorial onComplete={() => { tutorialResolved.current = true; setShowTutorial(false); setDbUser((prev) => prev ? { ...prev, has_seen_tutorial: true } : prev); }} />}

      {/* FIFA WC 2026 — one-time launch interstitial + Tier-1 country capture */}
      <LaunchInterstitial />
      <CountryModal />
      
      {/* Top Bar - always visible except on auth page */}
      {showNav && <TopBar />}
      
      {/* Banner Ad - shows at top on native platforms */}
      {showNav && <AdBanner />}
      
      {/* Bottom Navigation - 5 tabs */}
      {showNav && <Navigation />}
      
      <main 
        className={`max-w-7xl mx-auto px-4 py-6 ${showNav ? "pb-24" : ""}`} 
        style={{ 
          paddingTop: showNav ? "calc(4rem + env(safe-area-inset-top, 0px))" : undefined,
          paddingBottom: showNav ? "calc(6rem + env(safe-area-inset-bottom, 0px))" : undefined 
        }}
      >
        <Routes>
          <Route path="/" element={<HomeRouter />} />
          <Route path="/football/squad-builder" element={<ProtectedRoute><FootballSquadBuilderPage /></ProtectedRoute>} />
          <Route path="/football/squad-builder/:matchDay" element={<ProtectedRoute><FootballSquadBuilderPage /></ProtectedRoute>} />
          <Route path="/football/predictor" element={<ProtectedRoute><FootballPredictorPage /></ProtectedRoute>} />
          <Route path="/football/predictor/history" element={<ProtectedRoute><FootballPredictorHistoryPage /></ProtectedRoute>} />
          <Route path="/football/leaderboard" element={<ProtectedRoute><FootballLeaderboardPage /></ProtectedRoute>} />
          <Route path="/football/matches" element={<ProtectedRoute><FootballMatchesPage /></ProtectedRoute>} />
          <Route path="/football/fixtures/:id" element={<ProtectedRoute><FootballFixtureDetailPage /></ProtectedRoute>} />
          <Route path="/football/point-system" element={<FootballPointSystemPage />} />
          <Route path="/football/how-it-works" element={<HowFootballWorksPage />} />
          <Route path="/football/leagues" element={<ProtectedRoute><FootballLeaguesPage /></ProtectedRoute>} />
          <Route path="/football/leagues/:id" element={<ProtectedRoute><FootballLeagueDetailPage /></ProtectedRoute>} />
          <Route path="/admin/football/match-day" element={<ProtectedRoute><AdminFootballMatchDayPage /></ProtectedRoute>} />
          <Route path="/admin/football/users" element={<ProtectedRoute><AdminFootballUsersPage /></ProtectedRoute>} />
          <Route path="/admin/football/squads" element={<ProtectedRoute><AdminFootballSquadsPage /></ProtectedRoute>} />
          <Route path="/admin/football/weeks" element={<ProtectedRoute><AdminFootballWeeksPage /></ProtectedRoute>} />
          <Route path="/football/results/:matchDay" element={<ProtectedRoute><FootballMatchDayResultsPage /></ProtectedRoute>} />
          <Route path="/football/mini-cup" element={<ProtectedRoute><MiniCupPage /></ProtectedRoute>} />
          <Route path="/admin/football/mini-cup-sponsor" element={<ProtectedRoute><AdminMiniCupSponsorPage /></ProtectedRoute>} />
          <Route path="/auth" element={user ? <Navigate to="/" replace /> : <AuthPage />} />
          <Route path="/matches" element={<MatchesPage />} />
          <Route path="/players" element={<PlayersPage />} />
          <Route path="/daily" element={<MatchDayPage />} />
          <Route path="/daily/team/:matchId" element={<ProtectedRoute><DailyTeamBuilderPage /></ProtectedRoute>} />
          <Route path="/daily/history" element={<ProtectedRoute><DailyHistoryPage /></ProtectedRoute>} />
          <Route path="/my-team" element={<ProtectedRoute><TeamBuilderPage /></ProtectedRoute>} />
          <Route path="/leagues" element={<LeaguesPage />} />
          <Route path="/my-leagues" element={<LeaguesPage />} />
          <Route path="/my-leagues/:leagueId" element={<ProtectedRoute><LeagueDetailPage /></ProtectedRoute>} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/points" element={<PointsSystemPage />} />
          <Route path="/point-system" element={<PointSystemPage />} />
          <Route path="/predictor" element={<ProtectedRoute><PredictorPage /></ProtectedRoute>} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/subscribe" element={<SubscribePage />} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/football/pitch-preview" element={<FootballPitchPreviewPage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalyticsPage /></ProtectedRoute>} />
          <Route path="/admin/emails" element={<ProtectedRoute><AdminEmailsPage /></ProtectedRoute>} />
          <Route path="/admin/audience" element={<ProtectedRoute><AdminAudiencePage /></ProtectedRoute>} />
        </Routes>

        {/* Global Footer - web only, all pages except auth */}
        {showNav && !isNativePlatform && (
          <footer className="mt-8 pt-4 pb-2 border-t border-slate-800" data-testid="global-footer">
            <div className="flex items-center justify-center gap-3 text-xs text-slate-500 mb-3">
              <Link to="/privacy" className="hover:text-slate-400">Privacy Policy</Link>
              <span>·</span>
              <a href="mailto:support@apnapremierleague.com" className="hover:text-slate-400">Contact Us</a>
              <span>·</span>
              <span>v1.0.0</span>
            </div>
            <div className="flex items-center justify-center gap-4">
              <a href="https://www.instagram.com/apl.fantasycricket" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-pink-400 transition-colors" data-testid="footer-instagram" aria-label="Instagram">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://apps.apple.com/app/id6760789788" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white transition-colors" data-testid="footer-appstore" aria-label="App Store">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.apnapremierleague.app" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-emerald-400 transition-colors" data-testid="footer-playstore" aria-label="Play Store">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg>
              </a>
            </div>
          </footer>
        )}
      </main>
    </>
  );
}

export default App;
